import {
  Activity,
  HeartPulse,
  Ruler,
  Scaling,
  Thermometer,
  Weight,
  Wind,
} from "lucide-react";
import { z } from "zod";

// Backend enum BloodGroup — labels here are the correct clinical strings,
// not the ones the enum's own (buggy) getBloodGroup() returns for AB-.
export const BLOOD_GROUP_OPTIONS = [
  { value: "A_PLUS", label: "A+" },
  { value: "A_NEG", label: "A-" },
  { value: "B_PLUS", label: "B+" },
  { value: "B_NEG", label: "B-" },
  { value: "AB_PLUS", label: "AB+" },
  { value: "AB_NEG", label: "AB-" },
  { value: "O_PLUS", label: "O+" },
  { value: "O_NEG", label: "O-" },
  { value: "UNKNOWN", label: "Unknown" },
];

// Every field the backend PatientBaseline entity supports, grouped for
// display and for the add/edit form.
//
// Note: Blood Pressure (systolic/diastolic) has been removed from the
// baseline form — it is captured during CDM/OCF/PP reviews and other
// clinical workflows instead.
//
// Shared between the Baseline tab's "Add Baseline" dialog
// (BaselineDetails.jsx) and the consult's own Add Baseline tab
// (features/consultations/components/tabs/BaselineTab.jsx) so the two
// forms can never drift apart on which fields exist.
export const FIELD_SECTIONS = [
  {
    section: "Vitals",
    icon: Activity,
    // `icon` is display-only — the tiles on the Baseline tab carry it, the form
    // doesn't read it.
    fields: [
      { name: "weightKg", label: "Weight (kg)", icon: Weight },
      { name: "heightCm", label: "Height (cm)", icon: Ruler },
      { name: "waistCm", label: "Waist (cm)", icon: Scaling },
      { name: "pulse", label: "Pulse (bpm)", icon: HeartPulse },
      { name: "pulseRhythm", label: "Pulse Rhythm", type: "text", icon: Activity },
      { name: "tempCelsius", label: "Temperature (°C)", icon: Thermometer },
      {
        name: "respiratoryRate",
        label: "Respiratory Rate (breaths/min)",
        icon: Wind,
      },
    ],
  },
];

export const baselineFormSchema = z.object({
  weightKg: z.string().optional(),
  heightCm: z.string().optional(),
  waistCm: z.string().optional(),
  pulse: z.string().optional(),
  pulseRhythm: z.string().optional(),
  tempCelsius: z.string().optional(),
  respiratoryRate: z.string().optional(),
  peakFlow: z.string().optional(),
  systolic: z.string().optional(),
  diastolic: z.string().optional(),
  cholesterol: z.string().optional(),
  hdl: z.string().optional(),
  lld: z.string().optional(),
  bloodGroup: z.string().optional(),
  abdominalCircumferenceCm: z.string().optional(),
  headCircumferenceCm: z.string().optional(),
});

export const BASELINE_DEFAULT_VALUES = Object.fromEntries(
  Object.keys(baselineFormSchema.shape).map((name) => [name, ""])
);

export const getBloodGroupLabel = (value) =>
  BLOOD_GROUP_OPTIONS.find((option) => option.value === value)?.label || "-";

// Fields left blank are omitted rather than sent as "": the backend can't read
// "" as its BloodGroup enum and rejected the whole save (KIN-2134).
export const toBaselinePayload = (values) =>
  Object.fromEntries(
    Object.entries(values).filter(
      ([, value]) => typeof value === "string" && value.trim() !== ""
    )
  );
